import {
  PhaseProjectTable,
  type PhaseProjectLifecycleAction,
} from "@/components/features/settings/defaults/matter-phase-projects/PhaseProjectTable";
import {
  ActionSplitButton,
  ActionConfirmationModal,
  Button,
  Input,
  LoadingSpinner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import {
  phaseProjectsQueryOptions,
  useActivatePhaseProject,
  useArchivePhaseProject,
  useDeletePhaseProject,
  useReactivatePhaseProject,
} from "@/lib/data/react-query/matters/matter-phase";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { MATTER_PHASE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import type { MatterPhaseProjectLifecycle, PhaseProject } from "@/lib/types";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Plus, Search, Settings2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";

export function PhaseProjectsView({
  onManageLibrary,
}: {
  onManageLibrary?: () => void;
}) {
  const { hasGrant } = useUser();
  const navigate = useNavigate({ from: "/matters/settings/phase-projects" });
  const listSearch = useSearch({
    from: "/_auth/matters/settings/phase-projects",
  });
  const [search, setSearch] = useState(listSearch.q ?? "");
  const searchTimeoutRef = useRef<number | null>(null);
  const lifecycle: MatterPhaseProjectLifecycle | "All" =
    (listSearch.lifecycle as MatterPhaseProjectLifecycle | undefined) ?? "All";
  const incidentTypeId = listSearch.incidentTypeId ?? "All";
  const pageNumber = listSearch.pageNumber ?? 1;
  const pageSize = listSearch.pageSize ?? 25;
  const [pendingAction, setPendingAction] = useState<{
    type: PhaseProjectLifecycleAction;
    project: PhaseProject;
  }>();

  const canCreate = hasGrant(
    MATTER_PHASE_PERMISSION_KEYS.phaseProject,
    "create",
  );
  const canUpdate = hasGrant(
    MATTER_PHASE_PERMISSION_KEYS.phaseProject,
    "update",
  );
  const canDelete = hasGrant(
    MATTER_PHASE_PERMISSION_KEYS.phaseProject,
    "delete",
  );
  const matterSettingsQuery = useQuery(retrieveMatterSettingsQueryOptions());
  const projectsQuery = useQuery(
    phaseProjectsQueryOptions({
      q: listSearch.q?.trim() || undefined,
      lifecycle: lifecycle === "All" ? undefined : lifecycle,
      incidentTypeId: incidentTypeId === "All" ? undefined : incidentTypeId,
      pageNumber,
      pageSize,
    }),
  );
  const activateProject = useActivatePhaseProject();
  const archiveProject = useArchivePhaseProject();
  const reactivateProject = useReactivatePhaseProject();
  const deleteProject = useDeletePhaseProject();

  useEffect(() => {
    if (searchTimeoutRef.current !== null) {
      window.clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    setSearch(listSearch.q ?? "");
    return () => {
      if (searchTimeoutRef.current !== null) {
        window.clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [listSearch.q]);

  const incidentTypes = useMemo(
    () =>
      (matterSettingsQuery.data?.data.incidentTypes ?? []).map((type) => ({
        ...type,
        label: type.label ?? "Unnamed Incident Type",
      })),
    [matterSettingsQuery.data?.data.incidentTypes],
  );
  const projects = projectsQuery.data?.data ?? [];
  const count = projectsQuery.data?.pagination.count ?? 0;
  const pages = Math.max(1, Math.ceil(count / pageSize));
  const projectIncidentTypeLabels = useMemo(
    () => new Map(incidentTypes.map((type) => [type.id, type.label])),
    [incidentTypes],
  );
  const isActionPending =
    activateProject.isPending ||
    archiveProject.isPending ||
    reactivateProject.isPending ||
    deleteProject.isPending;

  const requestLifecycleAction = (
    type: PhaseProjectLifecycleAction,
    project: PhaseProject,
  ) => {
    if (type === "activate") {
      const revision = project.currentRevision;
      if (
        revision.incidentTypeIds.length === 0 ||
        revision.phases.length === 0 ||
        revision.phases.some(
          (phase) => phase.actions.length === 0 && !phase.widgets?.length,
        )
      ) {
        toast.error(
          "Add at least one Incident Type and one Phase, with an Action or Widget in each Phase, before activation.",
        );
        return;
      }
    }
    setPendingAction({ type, project });
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-64 xl:w-80">
          <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(event) => {
              const nextSearch = event.target.value;
              setSearch(nextSearch);
              if (searchTimeoutRef.current !== null) {
                window.clearTimeout(searchTimeoutRef.current);
              }
              searchTimeoutRef.current = window.setTimeout(() => {
                const nextQuery = nextSearch.trim();
                if (nextQuery === (listSearch.q ?? "").trim()) return;
                void navigate({
                  to: "/matters/settings/phase-projects",
                  search: (previous) => ({
                    ...previous,
                    q: nextQuery || undefined,
                    pageNumber: undefined,
                  }),
                  replace: true,
                });
              }, 250);
            }}
            className="bg-background pl-9"
            placeholder="Search phase projects"
          />
        </div>
        <Select
          value={lifecycle}
          onValueChange={(value) => {
            void navigate({
              to: "/matters/settings/phase-projects",
              search: (previous) => ({
                ...previous,
                lifecycle:
                  value === "All"
                    ? undefined
                    : (value as MatterPhaseProjectLifecycle),
                pageNumber: undefined,
              }),
              replace: true,
            });
          }}
        >
          <SelectTrigger className="bg-background w-full sm:w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="All">All lifecycles</SelectItem>
            <SelectItem value="Draft">Draft</SelectItem>
            <SelectItem value="Active">Active</SelectItem>
            <SelectItem value="Archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={incidentTypeId}
          onValueChange={(value) => {
            void navigate({
              to: "/matters/settings/phase-projects",
              search: (previous) => ({
                ...previous,
                incidentTypeId: value === "All" ? undefined : value,
                pageNumber: undefined,
              }),
              replace: true,
            });
          }}
        >
          <SelectTrigger className="bg-background w-full sm:w-52">
            <SelectValue placeholder="All incident types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="All">All incident types</SelectItem>
            {incidentTypes.map((type) => (
              <SelectItem key={type.id} value={type.id}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {canCreate ? (
          <div className="ml-auto shrink-0">
            <ActionSplitButton
              label="Create project"
              icon={Plus}
              onClick={() => {
                void navigate({
                  to: "/matters/settings/phase-projects",
                  search: (previous) => ({ ...previous, editor: "new" }),
                });
              }}
              actions={
                onManageLibrary
                  ? [
                      {
                        label: "Manage phases and actions",
                        icon: Settings2,
                        onSelect: onManageLibrary,
                      },
                    ]
                  : []
              }
            />
          </div>
        ) : onManageLibrary ? (
          <Button
            type="button"
            variant="outline"
            className="ml-auto"
            onClick={onManageLibrary}
          >
            Manage phases and actions
          </Button>
        ) : null}
      </div>

      {projectsQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner className="h-6 w-6" />
        </div>
      ) : projectsQuery.isError || matterSettingsQuery.isError ? (
        <Text variant="subHeading">
          Phase projects could not be loaded. Please try again.
        </Text>
      ) : (
        <div className="relative">
          <PhaseProjectTable
            projects={projects}
            incidentTypeLabels={projectIncidentTypeLabels}
            canUpdate={canUpdate}
            canDelete={canDelete}
            isFetching={projectsQuery.isFetching}
            pagination={{ pageIndex: pageNumber - 1, pageSize }}
            pageCount={pages}
            rowCount={count}
            onPaginationChange={(pagination) => {
              void navigate({
                to: "/matters/settings/phase-projects",
                search: (previous) => ({
                  ...previous,
                  pageNumber:
                    pagination.pageIndex > 0
                      ? pagination.pageIndex + 1
                      : undefined,
                  pageSize:
                    pagination.pageSize !== 25
                      ? pagination.pageSize
                      : undefined,
                }),
              });
            }}
            onOpen={(selectedProject) => {
              void navigate({
                to: "/matters/settings/phase-projects",
                search: (previous) => ({
                  ...previous,
                  editor: selectedProject.id,
                }),
              });
            }}
            onEdit={(selectedProject) => {
              void navigate({
                to: "/matters/settings/phase-projects",
                search: (previous) => ({
                  ...previous,
                  editor: selectedProject.id,
                  edit: true,
                }),
              });
            }}
            onLifecycleAction={requestLifecycleAction}
          />
        </div>
      )}

      <ActionConfirmationModal
        open={Boolean(pendingAction)}
        setOpen={(open) => {
          if (!open) setPendingAction(undefined);
        }}
        title={
          pendingAction
            ? `${pendingAction.type[0].toUpperCase()}${pendingAction.type.slice(1)} ${pendingAction.project.title}?`
            : undefined
        }
        description={
          pendingAction?.type === "activate"
            ? "This makes the Project available for its Incident Types. A Incident Type can only belong to one Active Project."
            : pendingAction?.type === "archive"
              ? "Archiving stops automatic application and releases the Project's Incident Type selections. Existing Matters keep their applied Phases."
              : pendingAction?.type === "reactivate"
                ? "This makes the archived Project available again for its Incident Types."
                : "Only a never-active Draft can be deleted. This action cannot be undone."
        }
        buttonText={pendingAction?.type}
        actionVariant={
          pendingAction?.type === "activate" ||
          pendingAction?.type === "reactivate"
            ? "default"
            : "destructive"
        }
        isActionPending={isActionPending}
        afterActionFn={async () => {
          if (!pendingAction) return;
          const variables = {
            projectId: pendingAction.project.id,
            dateModified: pendingAction.project.dateModified,
          };
          if (pendingAction.type === "activate") {
            await activateProject.mutateAsync(variables);
          } else if (pendingAction.type === "archive") {
            await archiveProject.mutateAsync(variables);
          } else if (pendingAction.type === "reactivate") {
            await reactivateProject.mutateAsync(variables);
          } else {
            await deleteProject.mutateAsync(pendingAction.project.id);
          }
          setPendingAction(undefined);
        }}
      />
    </div>
  );
}
